import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { CommonService } from 'src/app/services/Common-function/common.service';
import { PlatformSaleService } from 'src/app/services/platform-sale/platform-sale.service';

/** Stats refresh while the strip is open */
const STATS_REFRESH_INTERVAL = 120000;

interface StripStat {
  icon: string;
  value: string;
  label: string;
  shortLabel: string;
}

/**
 * Diwali sale strip (shared, top of the page): "LIVE" pill (sale running) or "SOON", live numbers from the sale stats API
 * (active users and beats enrolled), and the sale date, with a close button.
 * Dates come from startAt / endAt (the active platform sale). Chips with 0 are hidden.
 * Usage: <ipm-diwali-sale-strip [startAt]="..." [endAt]="..." (closed)="..."></ipm-diwali-sale-strip>
 */
@Component({
  selector: 'ipm-diwali-sale-strip',
  templateUrl: './diwali-sale-strip.component.html',
  styleUrls: ['./diwali-sale-strip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiwaliSaleStripComponent implements OnInit, OnDestroy {
  @Input() startAt: string | null = null;
  @Input() endAt: string | null = null;
  /** Show the "45% off Pro Annual" link (producers not on Pro Annual) */
  @Input() showProOffer: boolean = false;
  /** Close (x) button clicked: the parent hides the strip */
  @Output() closed = new EventEmitter<void>();
  /** "45% off Pro Annual" clicked: the parent opens the Diwali Pro popup */
  @Output() proOffer = new EventEmitter<void>();

  stats: Array<StripStat> = [];

  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private commonService: CommonService,
    private platformSaleService: PlatformSaleService,
    private changeDetector: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.loadStats();
    // sale start / end refresh (app.component): redraw LIVE / SOON and the date right away
    this.commonService.platformSaleData.pipe(takeUntil(this.destroy$)).subscribe(() => this.changeDetector.markForCheck());
    this.ngZone.runOutsideAngular(() => {
      // refresh the numbers (and the LIVE / SOON state) while the strip is open
      this.refreshTimer = setInterval(() => this.loadStats(), STATS_REFRESH_INTERVAL);
    });
  }

  ngOnDestroy(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Sale running: now between startAt and endAt */
  get isLive(): boolean {
    const now = Date.now();
    return !!this.startAt && !!this.endAt && now >= new Date(this.startAt).getTime() && now <= new Date(this.endAt).getTime();
  }

  /** Date on the last pill: the end date while the sale runs, the start date before */
  get dateText(): string {
    const date = this.isLive ? this.endAt : this.startAt;
    if (!date) {
      return '';
    }
    const formatted = new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    return this.isLive ? `Ends ${formatted}` : formatted;
  }

  private loadStats(): void {
    const saleId = this.commonService.getActivePlatformSale()?._id;
    if (!saleId) {
      return;
    }
    this.platformSaleService.getPlatFormStatsDetails(saleId, false).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      const stats = res?.data?.stats || {};
      const all: Array<StripStat & { count: number }> = [
        { icon: '👥', count: Number(stats.active_users) || 0, label: 'live users', shortLabel: 'live users', value: '' },
        { icon: '🎵', count: Number(stats.beats_listed) || 0, label: 'beats enrolled', shortLabel: 'beats', value: '' },
      ];
      // short numbers like the rest of the site: 950, 1.3K, 10K, 100K
      this.stats = all.filter((stat) => stat.count > 0).map(({ count, ...stat }) => ({ ...stat, value: this.commonService.nFormatter(count, 1) }));
      this.changeDetector.detectChanges();
    });
  }
}
